import { z } from 'zod';
import { ORG_ROLES, PROJECT_ROLES } from '../enums';
import { isoDate, uuid } from './common';

export const updateProfileSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  timezone: z.string().min(1).max(64).optional(),
  hoursPerDay: z.number().min(1).max(24).optional(),
  workingDays: z.array(z.number().int().min(1).max(7)).min(1).max(7).optional(),
  deputyUserId: uuid.nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const adminUpdateUserSchema = updateProfileSchema.extend({
  orgRole: z.enum(ORG_ROLES).optional(),
  isActive: z.boolean().optional(),
});
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

export const inviteUserSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(1).max(120),
  orgRole: z.enum(ORG_ROLES).default('member'),
});
export type InviteUserInput = z.infer<typeof inviteUserSchema>;

export const teamSchema = z.object({
  name: z.string().min(1).max(120),
  handle: z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{1,40}$/, 'Lowercase letters, digits and dashes'),
  memberIds: z.array(uuid).default([]),
});
export type TeamInput = z.infer<typeof teamSchema>;

export const projectMemberSchema = z.object({
  userId: uuid,
  role: z.enum(PROJECT_ROLES),
});
export type ProjectMemberInput = z.infer<typeof projectMemberSchema>;

export const leaveSchema = z.object({
  dateFrom: isoDate,
  dateTo: isoDate,
  type: z.enum(['leave', 'holiday', 'sick']).default('leave'),
});
export type LeaveInput = z.infer<typeof leaveSchema>;

/** Admins record leave for others (FR-IAM-6); the leave calendar drives capacity and deputy routing. */
export const adminLeaveSchema = leaveSchema.extend({ userId: uuid });
export const leaveQuery = z.object({ from: isoDate, to: isoDate, userIds: z.string().optional() });

/** Break-glass org admin sign-in for IdP outages (FR-IAM-1). Audited; one active account at most. */
export const breakGlassLoginSchema = z.object({ email: z.string().email(), secret: z.string().min(24).max(512) });

/** Dev-only sign-in (disabled in production). SSO is the only sign-in method in production (FR-IAM-1). */
export const devLoginSchema = z.object({ email: z.string().email() });
